import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useGetPromoQuotaQuery } from '../../../../redux/api/fymAPI';
import { planNameMessages } from '../../config/music24PlanNames';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { postsMessages } from './postsMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsQuotaLineProps {
  accountId: number;
}

/**
 * "34 / 200 · Scheduled posts this month · Pro" (design assets-rethink c1 `.pq`, d3). The numbers
 * come from `GET promo/quota`; the plan name from the plan of the workspace (`music24Plans.ts`).
 * A plan with no limit shows the count only.
 */
const PostsQuotaLineComponent: React.FC<PostsQuotaLineProps> = ({ accountId }) => {
  const intl = useIntl();
  const { plan } = useMusic24Capabilities();
  const { data } = useGetPromoQuotaQuery({ accountId });
  if (data === undefined) {
    return null;
  }
  const { used, limit } = data.scheduledPost;
  const share = limit === null || limit <= 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <div className={styles.quota} data-testid="posts-quota">
      <b>
        {limit === null
          ? intl.formatMessage(postsMessages.quotaUsedNoLimit, { used })
          : intl.formatMessage(postsMessages.quotaUsed, { used, limit })}
      </b>
      {limit !== null && (
        <div
          className={styles.quotaBar}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={used}
        >
          <i style={{ width: `${share}%` }} />
        </div>
      )}
      <small>
        {intl.formatMessage(postsMessages.quotaCaption, { plan: intl.formatMessage(planNameMessages[plan]) })}
      </small>
    </div>
  );
};

export const PostsQuotaLine = TypedMemo(PostsQuotaLineComponent);
